import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Check from "@material-ui/icons/Check";
import ContactsOutlined from "@material-ui/icons/ContactsOutlined";
import PermContactCalendarOutlined from "@material-ui/icons/PermContactCalendarOutlined";
import ContactMailOutlined from "@material-ui/icons/ContactMailOutlined";
import CloudOutlined from "@material-ui/icons/CloudOutlined";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";

/**
 * Extensions page card for the Address Book extension (user.extensions.address_book): turns on the
 * third-party address books (Microsoft 365, Google, iCloud, Yahoo) in the Address Book / Select
 * Recipients modal. Same look as the File Attachment Rules card.
 */
const useStyles = makeStyles(theme => {
    const s = farShared(theme);
    const t = s.t;
    return {
        card: { border: "1px solid "+(t.dark ? "rgba(110,168,255,0.55)" : t.info.border)+" !important", textAlign: "left" },
        head: { display: "flex", alignItems: "flex-start", gap: "12px" },
        icon: {
            flexShrink: 0, width: "48px", height: "48px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: t.info.bg, color: t.dark ? t.link : t.accent, "& svg": { width: "28px", height: "28px" }
        },
        headText: { flex: 1, minWidth: 0 },
        sources: {
            listStyle: "none", margin: "4px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: "8px",
            "& li": {
                display: "inline-flex", alignItems: "center", gap: "6px", padding: "5px 10px", borderRadius: "999px", fontSize: "13px",
                border: "1px solid "+t.border, color: t.textStrong, backgroundColor: t.dark ? "rgba(255,255,255,0.03)" : t.surfaceAlt
            },
            "& li svg": { width: "16px", height: "16px", color: t.link }
        },
        yahoo: {
            display: "inline-flex", alignItems: "center", justifyContent: "center", width: "16px", height: "16px", borderRadius: "3px",
            backgroundColor: "#6001D2", color: "#FFFFFF", fontSize: "8px", fontWeight: 800, letterSpacing: "-0.5px"
        },
        bullets: {
            listStyle: "none", margin: "4px 0 0", padding: "12px 14px", borderRadius: "8px", backgroundColor: t.dark ? "rgba(255,255,255,0.03)" : t.surfaceAlt,
            display: "flex", flexDirection: "column", gap: "6px",
            "& li": { display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "14px", lineHeight: "20px", color: t.text },
            "& svg": { width: "18px", height: "18px", color: t.success.solid, flexShrink: 0, marginTop: "1px" }
        },
        actions: { display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" },
        switchWrap: { marginLeft: "auto" },
        status: { fontSize: "13px", color: t.textMuted }
    };
});

function AddressBookExtensionCard({ enabled, switchControl, canManage }){
    const classes = useStyles();
    return (
        <div className={"extension "+classes.card}>
            <div className={classes.head}>
                <span className={classes.icon} aria-hidden="true"><ContactsOutlined /></span>
                <div className={classes.headText}>
                    <p className="title">Address Book</p>
                    <p className="description">Select recipients from your Microsoft 365, Google, iCloud and Yahoo contacts in the Sendlinx address book.</p>
                </div>
            </div>
            <ul className={classes.sources} aria-label="Supported address books">
                <li><PermContactCalendarOutlined aria-hidden="true" />Microsoft 365 / Outlook</li>
                <li><ContactMailOutlined aria-hidden="true" />Google / Gmail</li>
                <li><CloudOutlined aria-hidden="true" />iCloud</li>
                <li><span className={classes.yahoo} aria-hidden="true">Y!</span>Yahoo, Ymail, RocketMail & AOL</li>
            </ul>
            <ul className={classes.bullets}>
                <li><Check aria-hidden="true" />Adds these address books to the address book button when you send files, share links and share folders.</li>
                <li><Check aria-hidden="true" />You sign in to each provider on its own page; Sendlinx only reads contacts and never sees your passwords.</li>
                <li><Check aria-hidden="true" />Turning it off hides them and pauses syncing. Connected accounts stay connected until you disconnect them.</li>
            </ul>
            <div className={classes.actions}>
                { !canManage ? <span className={classes.status}>{enabled ? "Enabled by your administrator" : "Disabled by your administrator"}</span>
                    : <span className={classes.status}>Sendlinx contacts, recent recipients, favourites and groups are always available.</span> }
                <div className={classes.switchWrap}>{switchControl}</div>
            </div>
        </div>
    );
}

AddressBookExtensionCard.defaultProps = { enabled: false, canManage: true };
AddressBookExtensionCard.propTypes = {
    enabled: PropTypes.bool,
    switchControl: PropTypes.node.isRequired,
    canManage: PropTypes.bool
};

export default AddressBookExtensionCard;
